import axios, { AxiosInstance } from 'axios';

import { CampaignOutcome, CampaignTrigger, PageContext } from './types';

/**
 * IMBEE-8954 (T114 §6b, design.md "Two base URLs, on purpose") — the three campaign endpoints are
 * gateway-mounted on `REACT_APP_LIVE_CHAT_CAMPAIGN_GATEWAY_URL`, a DEDICATED env var, never a path
 * concatenated onto `REACT_APP_WEB_WIDGET_URL` (which stays pointed at `web-chat-service` for the
 * pre-existing `GET /widget` / `POST /sessions`, unchanged by this proposal). One axios instance
 * per callee, mirroring `apiClient/index.ts`'s own convention for the existing base URL.
 *
 * Every request body below is transcribed from the published api-document's request schemas
 * (`api-doc/web-chat-service.visitor.openapi.json` — external to this repo; `apiDocumentFixtures.ts`
 * states where it lives and which copy is authoritative), which are `additionalProperties: false`
 * and camelCase on the wire. An extra or mis-cased property is not a cosmetic difference here:
 * `ValidationPipe({whitelist: true})` strips what it does not recognise and then rejects the
 * now-missing required field, so a snake_case body is a 400 that REQ-CWC-04 folds into "no
 * campaign" — silently.
 */
export type CampaignIdentity = {
  getVisitorToken: () => string | null;
  getWidgetToken: () => string | null;
};

/**
 * IMBEE-8954 — an unresponsive gateway must not be able to hang the campaign controller's
 * evaluate/claim promise indefinitely: `runEvaluate`/`performClaim` only re-arm the NEXT trigger
 * once the current one settles (resolves or rejects), and a pending promise that never settles
 * would silently leave the whole feature never-armed for the rest of the page view — no error, no
 * log, nothing a human would see (the "silent failure" lens). REQ-CWC-04 already treats a
 * rejection as "no campaign"; a timeout is what turns "hung forever" into a rejection.
 */
const CAMPAIGN_REQUEST_TIMEOUT_MS = 8000;

export type EvaluateArgs = {
  pageContext: PageContext;
};

export type ClaimArgs = {
  campaignId: string;
  pageUrl: string;
  triggeredBy: CampaignTrigger;
  /**
   * AC-1.6 / AC-5.4 — the other candidates that became ready at the same instant. The server
   * recomputes every priority from its own documents and writes a `suppressedPriority` show for
   * each genuine loser; omitting this cannot change WHICH campaign wins, it simply means those
   * suppression records are never written and AC-5.4's breakdown is permanently short.
   */
  alsoReadyCampaignIds?: string[];
};

class CampaignApiClient {
  private client: AxiosInstance;

  private identity: CampaignIdentity;

  constructor(identity: CampaignIdentity) {
    this.identity = identity;
    this.client = axios.create({
      baseURL: process.env.REACT_APP_LIVE_CHAT_CAMPAIGN_GATEWAY_URL || '',
      timeout: CAMPAIGN_REQUEST_TIMEOUT_MS,
    });
  }

  private get headers() {
    return {
      'x-imbee-web-chat-token': this.identity.getWidgetToken() || '',
    };
  }

  /**
   * `EvaluationRequest` — read-only (design.md "Why evaluate/claim is two calls, not one"):
   * returns only an ordered candidate list + timing hints, never greeting content. Callers MUST
   * run the response through `parseEvaluateResponse` before trusting it (REQ-CWC-04).
   */
  async evaluate({ pageContext }: EvaluateArgs): Promise<unknown> {
    const response = await this.client.post(
      '/live-chat-campaign-evaluations',
      {
        visitorToken: this.identity.getVisitorToken(),
        pageUrl: pageContext.url,
        referrer: pageContext.referrer,
        isRouteChange: pageContext.isRouteChange,
      },
      { headers: this.headers },
    );
    return response.data;
  }

  /**
   * `ShowClaimRequest` — re-validates server-side and is the only call that can return greeting
   * content. Callers MUST run the response through `parseClaimResponse` before trusting it.
   */
  async claim({ campaignId, pageUrl, triggeredBy, alsoReadyCampaignIds }: ClaimArgs): Promise<unknown> {
    const response = await this.client.post(
      '/live-chat-campaign-shows',
      {
        visitorToken: this.identity.getVisitorToken(),
        campaignId,
        pageUrl,
        triggeredBy,
        ...(alsoReadyCampaignIds && alsoReadyCampaignIds.length ? { alsoReadyCampaignIds } : {}),
      },
      { headers: this.headers },
    );
    return response.data;
  }

  /** `OutcomeRequest`. Answers 204 with no body — nothing to parse. */
  async reportOutcome(showId: string, outcome: CampaignOutcome): Promise<void> {
    await this.client.post(
      `/live-chat-campaign-shows/${showId}/outcomes`,
      {
        visitorToken: this.identity.getVisitorToken(),
        outcome,
      },
      { headers: this.headers },
    );
  }
}

export default CampaignApiClient;
